import { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { initTelegramUi } from "../utils/telegram.js";
import { useMe } from "../hooks/useMe.js";
import { useIsDesktop } from "../hooks/useIsDesktop.js";
import { getInitData } from "../utils/initData.js";
import { IndexRedirectPage } from "./pages/IndexRedirectPage.jsx";
import { DialogsOnlyPage } from "./pages/DialogsOnlyPage.jsx";
import { ClientsPage } from "./pages/ClientsPage.jsx";
import { ReportsPage } from "./pages/ReportsPage.jsx";
import { ChatPage } from "./pages/ChatPage.jsx";
import { DealsPage } from "./pages/DealsPage.jsx";
import { DealCardPage } from "./pages/DealCardPage.jsx";
import { TasksPage } from "./pages/TasksPage.jsx";
import { SettingsPage } from "./pages/SettingsPage.jsx";
import { ProfilePage } from "./pages/ProfilePage.jsx";
import { BottomNav } from "./components/BottomNav.jsx";

function showBottomNav(pathname) {
  if (!pathname) return false;
  if (pathname === "/chat" || pathname.startsWith("/chat/")) return true;
  if (pathname === "/clients" || pathname === "/tasks" || pathname === "/reports" || pathname === "/settings" || pathname === "/profile") return true;
  if (pathname.startsWith("/deals")) return true;
  return false;
}

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { me, loading, isAdmin } = useMe();
  const isDesktop = useIsDesktop();
  const pathname = location.pathname || "/";
  const prevPathRef = useRef(pathname);
  const [overlayHideTrigger, setOverlayHideTrigger] = useState(0);
  const hasInitData = !!getInitData();

  useEffect(() => {
    initTelegramUi();
  }, []);

  if (!hasInitData) {
    return (
      <div className="pill" style={{ margin: "2rem", textAlign: "center" }}>
        Откройте приложение из Telegram
      </div>
    );
  }

  useEffect(() => {
    if (isDesktop) document.body.classList.add("layout-desktop");
    else document.body.classList.remove("layout-desktop");
    return () => document.body.classList.remove("layout-desktop");
  }, [isDesktop]);

  // Telegram Desktop / некоторые десктоп-WebView плохо скроллят вложенные контейнеры колесиком.
  // Принудительно прокручиваем ближайший "скролл-слой" (как в Диалогах) при fine-pointer (мышь/тачпад).
  useEffect(() => {
    const isFinePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: fine)").matches;
    if (!isFinePointer) return;

    function pickScrollEl(target) {
      if (!(target instanceof Element)) return null;
      if (target.closest(".customSelectDropdown")) return null;
      if (target.closest(".modal, .modalSheet, .modalBody")) return null;
      if (target.closest(".dealsKanbanScroll")) return null;
      const main = target.closest("main.chat");
      if (!main) return null;
      // 1) Универсальный слой
      const fromClosest = target.closest(".screenScroll");
      if (fromClosest) return fromClosest;
      // 2) Списки (как Диалоги)
      const list = main.querySelector("#list.crmListSurface");
      if (list) return list;
      // 3) Чат
      if (main.id === "chat") {
        const chatScroll = main.querySelector("#chatScroll");
        if (chatScroll) return chatScroll;
      }
      return null;
    }

    function onWheel(e) {
      if (e.defaultPrevented) return;
      // Ctrl+wheel = zoom; Shift+wheel часто используют для горизонтального скролла.
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      const dy = Number(e.deltaY || 0);
      const dx = Number(e.deltaX || 0);
      if (!dy || Math.abs(dy) < Math.abs(dx)) return;

      const scrollEl = pickScrollEl(e.target);
      if (!scrollEl) return;
      const maxTop = (scrollEl.scrollHeight || 0) - (scrollEl.clientHeight || 0);
      if (maxTop <= 1) return;

      const prev = scrollEl.scrollTop || 0;
      let next = prev + dy;
      if (next < 0) next = 0;
      if (next > maxTop) next = maxTop;
      if (next === prev) return;

      e.preventDefault();
      scrollEl.scrollTop = next;
    }

    document.addEventListener("wheel", onWheel, { capture: true, passive: false });
    return () => {
      document.removeEventListener("wheel", onWheel, { capture: true });
    };
  }, []);

  useEffect(() => {
    document.body.classList.remove("page-index", "page-list", "page-chat", "page-clients");
    if (pathname === "/") document.body.classList.add("page-index");
    else if (pathname.startsWith("/chat/")) document.body.classList.add("page-chat");
    else if (pathname === "/chat") document.body.classList.add("page-list");
    else if (pathname === "/settings") document.body.classList.add("page-list");
    else if (pathname === "/clients" || pathname === "/deals" || pathname.startsWith("/deals/") || pathname === "/tasks" || pathname === "/profile") document.body.classList.add("page-clients");
  }, [pathname]);

  useEffect(() => {
    if (pathname === prevPathRef.current) return;
    const t = setTimeout(() => {
      prevPathRef.current = pathname;
      setOverlayHideTrigger((n) => n + 1);
    }, 380);
    return () => clearTimeout(t);
  }, [pathname]);

  const routeOverlayActive = pathname !== prevPathRef.current;

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    const back = tg?.BackButton;
    if (!back) return;
    const p = location.pathname || "/";

    const shouldShow =
      p.startsWith("/chat/") ||
      p.startsWith("/deals/") ||
      p === "/settings" ||
      p === "/profile";

    if (shouldShow) back.show();
    else back.hide();

    const handler = () => {
      if (window.history.length > 1) navigate(-1);
      else navigate("/deals", { replace: true });
    };
    back.onClick(handler);
    return () => {
      try {
        back.offClick(handler);
      } catch {
        // ignore
      }
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    const btn = tg?.SettingsButton;
    if (!btn) return;

    const p = location.pathname || "/";
    const shouldShow = !loading && !!me && !!isAdmin && p !== "/settings";

    try {
      if (shouldShow) btn.show();
      else btn.hide();
    } catch {
      // ignore
    }

    const handler = () => {
      navigate("/settings");
    };

    try {
      btn.onClick(handler);
    } catch {
      // ignore
    }

    return () => {
      try {
        btn.offClick(handler);
      } catch {
        // ignore
      }
    };
  }, [isAdmin, loading, location.pathname, me, navigate]);

  return (
    <div className="appLayout">
      <div className={`routeOverlay ${routeOverlayActive ? "isActive" : ""}`} aria-hidden="true" />
      <div className="appContent">
        <Routes>
          <Route path="/" element={<IndexRedirectPage />} />
          <Route path="/admin" element={<Navigate to="/deals" replace />} />
          <Route path="/manager" element={<Navigate to="/deals" replace />} />
          <Route path="/chat" element={<DialogsOnlyPage />} />
          <Route path="/chat/:userId" element={<ChatPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/deals" element={<DealsPage />} />
          <Route path="/deals/:id" element={<DealCardPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {showBottomNav(pathname) ? <BottomNav /> : null}
    </div>
  );
}

